import { useCallback, useLayoutEffect, useRef } from 'react'
import type { PopoverSide } from './useClampToViewport'
import { usePopover } from './usePopover'

/**
 * A portalled popover kept against the trigger that opened it.
 *
 * The portal escapes every scroll region between the trigger and the window.
 * Its position therefore comes from the trigger's viewport box and is refreshed
 * whenever that box can move. `usePopover` remains the one owner of dismissal
 * and viewport clamping; this hook supplies the one shared anchor for portalled
 * menus.
 */
export function useAnchoredPopover<T extends HTMLElement>({
  align = 'start',
  side = 'forward',
}: {
  align?: 'start' | 'end'
  side?: PopoverSide
} = {}) {
  const popover = usePopover({ side })
  const triggerRef = useRef<T>(null)

  const place = useCallback(() => {
    const trigger = triggerRef.current
    const panel = popover.panelRef.current
    if (!trigger || !panel) return
    const box = trigger.getBoundingClientRect()
    const rtl = getComputedStyle(trigger).direction === 'rtl'
    const fromLeft = (align === 'start') !== rtl
    const anchor = {
      ['--popover-top' as string]: `${box.bottom}px`,
      ['--popover-bottom' as string]: `${window.innerHeight - box.top}px`,
      ['--popover-trigger-w' as string]: `${box.width}px`,
      ...(fromLeft
        ? { left: `${box.left}px`, right: 'auto' }
        : { left: 'auto', right: `${window.innerWidth - box.right}px` }),
    }
    for (const [property, value] of Object.entries(anchor))
      panel.style.setProperty(property, String(value))
  }, [align, popover.panelRef])

  useLayoutEffect(() => {
    if (!popover.open) return
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [popover.open, place])

  return { ...popover, triggerRef }
}
